import { createSelector } from '@reduxjs/toolkit';
import { useGetParentCommentsQuery } from 'features/comments/commentApi';
import { DateTime } from 'luxon';
import { useMemo } from 'react';

const emptyArray = [];

/**
 * @returns {{
 *     comments: import('models/comments.models').Comment[],
 *     unresolvedComments: import('models/comments.models').Comment[]
 * }}
 */
export function useGetCommentsFromParent(parentUri, contextUris = [], filterByLabels = [], until = undefined) {
    const findComments = useMemo(() => {
        // Return a unique selector instance for this page so that
        // the filtered results are correctly memoized
        return createSelector(
            (res) => res.data,
            (data, contextUris) => contextUris,
            (data, contextUris, filterByLabels) => filterByLabels,
            (data, contextUris, filterByLabels, until) => until,
            (data, contextUris = [], filterByLabels = [], until = undefined) => {
                if (!data) {
                    return emptyArray;
                }

                let comments = data;

                if (contextUris.length > 0) {
                    comments = comments.filter((comment) => contextUris.includes(comment.context));
                }

                if (filterByLabels.length > 0) {
                    comments = comments.filter((comment) =>
                        comment.tags.some((_labelUri) => filterByLabels.includes(_labelUri.uri)),
                    );
                }

                // If until is given, filter out any comments created AFTER the until timestamp
                if (until !== undefined) {
                    comments = comments.filter((comment) => DateTime.fromISO(comment.createdAt).toMillis() < until);
                }

                return comments;
            },
        );
    }, []);

    return useGetParentCommentsQuery(parentUri, {
        selectFromResult: (result) => {
            const comments = findComments(result, contextUris, filterByLabels, until);

            return {
                comments,
                unresolvedComments: comments.filter((comment) => !comment.resolved),
            };
        },
    });
}
